import React, { useEffect, useMemo, useState } from 'react'
import { api, SkillCategoryData, SkillItemWithCategory } from '@/services/api'
import { Modal } from '@/components/common'
import { LexicalMarkdownEditor } from '@/components/editor/LexicalMarkdownEditor'
import { parseMarkdown } from '@/utils/markdown'

type ViewMode = 'card' | 'list'

export const SkillsPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState<SkillCategoryData[]>([])
  const [items, setItems] = useState<SkillItemWithCategory[]>([])

  // Filter state
  const [filterCategoryId, setFilterCategoryId] = useState<string | null>(null)

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('card')

  // Expanded skill
  const [expandedSkillId, setExpandedSkillId] = useState<string | null>(null)

  // Category management modal
  const [showCategoryManageModal, setShowCategoryManageModal] = useState(false)
  const [showCategoryEditModal, setShowCategoryEditModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<SkillCategoryData | null>(null)
  const [categoryName, setCategoryName] = useState('')
  const [categoryIcon, setCategoryIcon] = useState('')

  // Skill modal
  const [showSkillModal, setShowSkillModal] = useState(false)
  const [editingSkill, setEditingSkill] = useState<SkillItemWithCategory | null>(null)
  const [skillName, setSkillName] = useState('')
  const [skillContent, setSkillContent] = useState('')
  const [skillCategoryId, setSkillCategoryId] = useState('')

  const reload = async () => {
    setLoading(true)
    try {
      // Fetch system skills and user skills in parallel
      const [systemCatsRes, systemItemsRes, userCatsRes, userItemsRes] = await Promise.all([
        api.skills.listSystemCategories(),
        api.skills.listSystemItems(),
        api.skills.listCategories(),
        api.skills.listItems(),
      ])

      // Merge categories: system first, then user's
      const systemCats = (systemCatsRes.data || []).filter(c => !c.is_deleted)
      const userCats = (userCatsRes.data || []).filter(c => !c.is_deleted && !c.is_system_default)
      setCategories([...systemCats, ...userCats])

      // Merge items: system first, then user's
      const systemItems = (systemItemsRes.data || []).filter(i => !i.is_deleted)
      const userItems = (userItemsRes.data || []).filter(i => !i.is_deleted && !i.is_system_default)
      setItems([...systemItems, ...userItems])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
  }, [])

  // Filtered items based on selected category
  const filteredItems = useMemo(() => {
    if (!filterCategoryId) return items
    return items.filter(item => item.category_id === filterCategoryId)
  }, [items, filterCategoryId])

  const handleSkillToggle = (skillId: string) => {
    setExpandedSkillId(expandedSkillId === skillId ? null : skillId)
  }

  // ========== Category Management ==========
  const openCategoryManagement = () => {
    setShowCategoryManageModal(true)
  }

  const openCreateCategory = () => {
    setEditingCategory(null)
    setCategoryName('')
    setCategoryIcon('')
    setShowCategoryEditModal(true)
  }

  const openEditCategory = (cat: SkillCategoryData) => {
    setEditingCategory(cat)
    setCategoryName(cat.name)
    setCategoryIcon(cat.icon || '')
    setShowCategoryEditModal(true)
  }

  const submitCategory = async () => {
    const name = categoryName.trim()
    if (!name) return
    try {
      if (editingCategory) {
        await api.skills.updateCategory(editingCategory.id, {
          name,
          icon: categoryIcon.trim() || null,
        })
      } else {
        await api.skills.createCategory({
          name,
          icon: categoryIcon.trim() || null,
        })
      }
      setShowCategoryEditModal(false)
      await reload()
    } catch (err: any) {
      alert(err?.message || 'Failed to save category')
    }
  }

  const deleteCategory = async (cat: SkillCategoryData) => {
    if (!confirm(`Delete category "${cat.name}" and all its skills?`)) return
    try {
      await api.skills.deleteCategory(cat.id)
      if (filterCategoryId === cat.id) {
        setFilterCategoryId(null)
      }
      await reload()
    } catch (err: any) {
      alert(err?.message || 'Failed to delete category')
    }
  }

  // ========== Skill CRUD ==========
  const openCreateSkill = () => {
    setEditingSkill(null)
    setSkillName('')
    setSkillContent('# Skill Title\n\nDescribe the skill here...')
    setSkillCategoryId(filterCategoryId || (categories.length > 0 ? categories[0].id : ''))
    setShowSkillModal(true)
  }

  const openEditSkill = (skill: SkillItemWithCategory, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingSkill(skill)
    setSkillName(skill.name)
    setSkillContent(skill.content_md)
    setSkillCategoryId(skill.category_id)
    setShowSkillModal(true)
  }

  const submitSkill = async () => {
    const name = skillName.trim()
    const content = skillContent.trim()
    if (!name || !content || !skillCategoryId) return
    try {
      if (editingSkill) {
        await api.skills.updateItem(editingSkill.id, {
          name,
          content_md: content,
          category_id: skillCategoryId,
        })
      } else {
        await api.skills.createItem({
          category_id: skillCategoryId,
          name,
          content_md: content,
        })
      }
      setShowSkillModal(false)
      await reload()
    } catch (err: any) {
      alert(err?.message || 'Failed to save skill')
    }
  }

  const deleteSkill = async (skill: SkillItemWithCategory, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm(`Delete skill "${skill.name}"?`)) return
    try {
      await api.skills.deleteItem(skill.id)
      await reload()
    } catch (err: any) {
      alert(err?.message || 'Failed to delete skill')
    }
  }

  // ========== Render ==========
  const chevronDown = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>'

  const renderCardView = () => (
    <div className="skill-card-grid">
      {filteredItems.map((skill) => {
        const isSystem = skill.is_system_default

        return (
          <div
            key={skill.id}
            className={`skill-card-item ${expandedSkillId === skill.id ? 'expanded' : ''} ${isSystem ? 'skill-card-item--system' : ''}`}
          >
            <div
              className="skill-card-item-header"
              onClick={() => handleSkillToggle(skill.id)}
            >
              <div className="skill-card-item-title">
                {skill.name}
                {isSystem && <span className="skill-system-badge">System</span>}
              </div>
              <div className="skill-card-item-tag">
                {skill.category_icon && (
                  <svg
                    className="icon"
                    style={{
                      width: 12,
                      height: 12,
                      marginRight: 4,
                      ...(skill.category_icon === 'twitter' ? { fill: 'currentColor', stroke: 'none' } : {}),
                    }}
                  >
                    <use href={`#icon-${skill.category_icon}`}></use>
                  </svg>
                )}
                {skill.category_name}
              </div>
            </div>
            <div className="skill-card-item-actions" onClick={(e) => e.stopPropagation()}>
              {!isSystem && (
                <>
                  <button
                    className="btn btn-ghost btn-sm"
                    title="Edit"
                    onClick={(e) => openEditSkill(skill, e)}
                  >
                    <svg className="icon" style={{ width: 14, height: 14 }}>
                      <use href="#icon-edit"></use>
                    </svg>
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    title="Delete"
                    onClick={(e) => deleteSkill(skill, e)}
                  >
                    <svg className="icon" style={{ width: 14, height: 14 }}>
                      <use href="#icon-trash"></use>
                    </svg>
                  </button>
                </>
              )}
              <div
                className="skill-card-item-toggle"
                onClick={() => handleSkillToggle(skill.id)}
                dangerouslySetInnerHTML={{ __html: chevronDown }}
              ></div>
            </div>
            <div className="skill-card-item-content">
              <div className="skill-card-item-inner">
                {isSystem ? (
                  <p className="skill-card-item-summary">{skill.summary || skill.name}</p>
                ) : (
                  <div
                    className="markdown-content"
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(skill.content_md || '') }}
                  />
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )

  const renderListView = () => (
    <div className="skill-flat-list">
      {filteredItems.map((skill) => {
        const isSystem = skill.is_system_default

        return (
          <div
            key={skill.id}
            className={`skill-flat-item ${expandedSkillId === skill.id ? 'expanded' : ''} ${isSystem ? 'skill-flat-item--system' : ''}`}
          >
            <div
              className="skill-flat-header"
              onClick={() => handleSkillToggle(skill.id)}
            >
              <div className="skill-flat-left">
                <div className="skill-flat-bullet"></div>
                <div className="skill-flat-title">
                  {skill.name}
                  {isSystem && <span className="skill-system-badge">System</span>}
                </div>
              </div>
              <div className="skill-flat-right">
                <div className="skill-flat-tag">
                  {skill.category_icon && (
                    <svg
                      className="icon"
                      style={{
                        width: 12,
                        height: 12,
                        marginRight: 4,
                        ...(skill.category_icon === 'twitter' ? { fill: 'currentColor', stroke: 'none' } : {}),
                      }}
                    >
                      <use href={`#icon-${skill.category_icon}`}></use>
                    </svg>
                  )}
                  {skill.category_name}
                </div>
                {!isSystem && (
                  <div className="skill-flat-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn btn-ghost btn-sm"
                      title="Edit"
                      onClick={(e) => openEditSkill(skill, e)}
                    >
                      <svg className="icon" style={{ width: 14, height: 14 }}>
                        <use href="#icon-edit"></use>
                      </svg>
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      title="Delete"
                      onClick={(e) => deleteSkill(skill, e)}
                    >
                      <svg className="icon" style={{ width: 14, height: 14 }}>
                        <use href="#icon-trash"></use>
                      </svg>
                    </button>
                  </div>
                )}
                <div
                  className="skill-flat-toggle"
                  dangerouslySetInnerHTML={{ __html: chevronDown }}
                ></div>
              </div>
            </div>
            <div className="skill-flat-content">
              <div className="skill-flat-inner">
                {isSystem ? (
                  <p className="skill-flat-item-summary">{skill.summary || skill.name}</p>
                ) : (
                  <div
                    className="markdown-content"
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(skill.content_md || '') }}
                  />
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <>
      <div className="main-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 className="main-title">Skills Library</h1>
          {loading && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading...</span>}
        </div>
      </div>
      <div className="main-body">
        <p className="skills-intro">
          GEO Skills are writing techniques proven to increase AI citation rates. Click any skill to view details.
        </p>

        {/* Filter toolbar with actions */}
        <div className="skills-toolbar">
          {/* Left: Category filter tabs */}
          <div className="skills-filter-tabs">
            <button
              className={`skills-filter-tab ${filterCategoryId === null ? 'active' : ''}`}
              onClick={() => setFilterCategoryId(null)}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`skills-filter-tab ${filterCategoryId === cat.id ? 'active' : ''}`}
                onClick={() => setFilterCategoryId(cat.id)}
              >
                {cat.icon && (
                  <svg
                    className="icon"
                    style={{
                      width: 14,
                      height: 14,
                      marginRight: 4,
                      ...(cat.icon === 'twitter' ? { fill: 'currentColor', stroke: 'none' } : {}),
                    }}
                  >
                    <use href={`#icon-${cat.icon}`}></use>
                  </svg>
                )}
                {cat.name}
              </button>
            ))}
          </div>

          {/* Right: Actions */}
          <div className="skills-actions">
            <button className="btn btn-primary btn-sm" onClick={openCreateSkill}>
              + Add Skill
            </button>
            <button className="btn btn-secondary btn-sm" onClick={openCategoryManagement}>
              Add Category
            </button>
            <div className="view-toggle-group">
              <button
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <svg className="icon" style={{ width: 16, height: 16 }}>
                  <use href="#icon-list"></use>
                </svg>
              </button>
              <button
                className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
                onClick={() => setViewMode('card')}
                title="Card view"
              >
                <svg className="icon" style={{ width: 16, height: 16 }}>
                  <use href="#icon-grid"></use>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Skills display */}
        <div className={`skills-content ${viewMode === 'list' ? 'centered' : ''}`}>
          {viewMode === 'card' ? renderCardView() : renderListView()}

          {filteredItems.length === 0 && !loading && (
            <div style={{ color: 'var(--text-muted)', fontSize: 14, padding: '20px 0', textAlign: 'center' }}>
              {filterCategoryId
                ? 'No skills in this category. Click "+ Add Skill" to create one.'
                : 'No skills yet. Click "+ Add Skill" to create one.'}
            </div>
          )}
        </div>
      </div>

      {/* Category Management Modal */}
      <Modal
        show={showCategoryManageModal}
        onClose={() => setShowCategoryManageModal(false)}
        title="Manage Categories"
        size="md"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ marginBottom: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={openCreateCategory}>
              + Add Category
            </button>
          </div>
          <div className="category-manage-list">
            {categories.map((cat) => (
              <div key={cat.id} className="category-manage-item">
                <div className="category-manage-info">
                  {cat.icon && (
                    <svg
                      className="icon"
                      style={{
                        width: 16,
                        height: 16,
                        marginRight: 8,
                        ...(cat.icon === 'twitter' ? { fill: 'currentColor', stroke: 'none' } : {}),
                      }}
                    >
                      <use href={`#icon-${cat.icon}`}></use>
                    </svg>
                  )}
                  <span>{cat.name}</span>
                  {cat.is_system_default && (
                    <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 6, fontStyle: 'italic' }}>System</span>
                  )}
                  <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 8 }}>
                    ({items.filter(i => i.category_id === cat.id).length} skills)
                  </span>
                </div>
                {!cat.is_system_default && (
                  <div className="category-manage-actions">
                    <button
                      className="btn btn-ghost btn-sm"
                      title="Edit"
                      onClick={() => openEditCategory(cat)}
                    >
                      <svg className="icon" style={{ width: 14, height: 14 }}>
                        <use href="#icon-edit"></use>
                      </svg>
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      title="Delete"
                      onClick={() => deleteCategory(cat)}
                    >
                      <svg className="icon" style={{ width: 14, height: 14 }}>
                        <use href="#icon-trash"></use>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ))}
            {categories.length === 0 && (
              <div style={{ color: 'var(--text-muted)', fontSize: 14, padding: '12px 0' }}>
                No categories yet.
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowCategoryManageModal(false)}>
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Category Edit Modal */}
      <Modal
        show={showCategoryEditModal}
        onClose={() => setShowCategoryEditModal(false)}
        title={editingCategory ? 'Edit Category' : 'New Category'}
        size="md"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="form-group">
            <label>Name</label>
            <input
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="e.g. Blog, Twitter, LinkedIn"
            />
          </div>
          <div className="form-group">
            <label>Icon (optional)</label>
            <input
              value={categoryIcon}
              onChange={(e) => setCategoryIcon(e.target.value)}
              placeholder="e.g. blog, twitter, linkedin, reddit"
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Available: blog, twitter, linkedin, reddit, sparkles
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowCategoryEditModal(false)}>
              Cancel
            </button>
            <button className="btn btn-primary btn-sm" onClick={submitCategory}>
              Save
            </button>
          </div>
        </div>
      </Modal>

      {/* Skill Modal */}
      <Modal
        show={showSkillModal}
        onClose={() => setShowSkillModal(false)}
        title={editingSkill ? 'Edit Skill' : 'New Skill'}
        size="xxl"
        className="skill-edit-modal"
        headerRight={
          <select
            value={skillCategoryId}
            onChange={(e) => setSkillCategoryId(e.target.value)}
            className="skill-category-select"
          >
            <option value="">Select category...</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        }
      >
        <div className="skill-edit-form">
          <div className="skill-edit-name-group">
            <label>Skill Name</label>
            <input
              value={skillName}
              onChange={(e) => setSkillName(e.target.value)}
              placeholder="e.g. Inverted Pyramid, Hook Opening"
              className="skill-edit-name-input"
            />
          </div>
          <div className="skill-edit-content-group">
            <label>Content (Markdown)</label>
            <LexicalMarkdownEditor
              initialMarkdown={skillContent}
              onMarkdownChange={setSkillContent}
              placeholder="Write skill guidance in Markdown..."
              minHeight={450}
              maxHeight={600}
            />
          </div>
          <div className="skill-edit-actions">
            <button className="btn btn-secondary" onClick={() => setShowSkillModal(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={submitSkill}>
              Save
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}
